import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, 
  Plus, 
  FolderPlus, 
  Edit3, 
  Save, 
  X, 
  Download, 
  Upload, 
  Trash2,
  Folder,
  ChevronDown,
  ChevronRight,
  Search,
  Sun,
  Moon,
  HelpCircle,
  FilePlus,
  Menu,
  ArrowLeft,
  FileDown,
  FileText as FileTextIcon
} from 'lucide-react';
import logo from './logo.svg';


// Types
interface MarkdownDoc {
  id: string;
  title: string;
  content: string;
  group: string;
  createdAt: Date;
  updatedAt: Date;
}

interface Group {
  id: string;
  name: string;
  expanded: boolean;
}

// IndexedDB utility functions
const DB_NAME = 'MarkdownDocsDB';
const DB_VERSION = 1;

const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      if (!db.objectStoreNames.contains('documents')) {
        const docStore = db.createObjectStore('documents', { keyPath: 'id' });
        docStore.createIndex('group', 'group', { unique: false });
      }
      
      if (!db.objectStoreNames.contains('groups')) {
        db.createObjectStore('groups', { keyPath: 'id' });
      }
    };
  });
};

const saveDocument = async (doc: MarkdownDoc): Promise<void> => {
  const db = await initDB();
  const transaction = db.transaction(['documents'], 'readwrite');
  const store = transaction.objectStore('documents');
  await store.put(doc);
};

const getDocuments = async (): Promise<MarkdownDoc[]> => {
  const db = await initDB();
  const transaction = db.transaction(['documents'], 'readonly');
  const store = transaction.objectStore('documents');
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const deleteDocument = async (id: string): Promise<void> => {
  const db = await initDB();
  const transaction = db.transaction(['documents'], 'readwrite');
  const store = transaction.objectStore('documents');
  await store.delete(id);
};

const saveGroups = async (groups: Group[]): Promise<void> => {
  const db = await initDB();
  const transaction = db.transaction(['groups'], 'readwrite');
  const store = transaction.objectStore('groups');
  await store.clear();
  for (const group of groups) {
    await store.add(group);
  }
};

const getGroups = async (): Promise<Group[]> => {
  const db = await initDB();
  const transaction = db.transaction(['groups'], 'readonly');
  const store = transaction.objectStore('groups');
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Enhanced Markdown parser with more features
const parseMarkdown = (content: string): string => {
  let html = content;
  
  // Escape HTML (mas preservar blocos de código)
  // Primeiro, vamos marcar os blocos de código para não escapá-los
  const codeBlocks: string[] = [];
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/gim, (match, lang, code) => {
    const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
    codeBlocks.push(match);
    return placeholder;
  });
  
  // Agora fazer o escape HTML
  html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  
  // Restaurar os blocos de código
  codeBlocks.forEach((block, index) => {
    html = html.replace(`__CODE_BLOCK_${index}__`, block);
  });
  
  // Code blocks (must be done before other parsing)
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/gim, (match, lang, code) => {
    const language = lang || 'text';
    
    // PRESERVAR a indentação original - NÃO normalizar!
    // Remover apenas a primeira linha em branco se existir
    let formattedCode = code;
    if (formattedCode.startsWith('\n')) {
      formattedCode = formattedCode.substring(1);
    }
    
    // Detectar automaticamente a linguagem baseado no conteúdo
    let detectedLang = language;
    if (language === 'text') {
      if (formattedCode.includes('SELECT') || formattedCode.includes('UPDATE') || formattedCode.includes('INSERT')) {
        detectedLang = 'sql';
      } else if (formattedCode.includes('function') || formattedCode.includes('const') || formattedCode.includes('let')) {
        detectedLang = 'javascript';
      } else if (formattedCode.includes('{') && formattedCode.includes('"')) {
        detectedLang = 'json';
      } else if (formattedCode.includes('database:') || formattedCode.includes('logging:')) {
        detectedLang = 'yaml';
      }
    }
    
    // IMPORTANTE: Não escapar o código aqui, pois já foi preservado
    // Garantir que a indentação seja preservada usando CSS
    return `<pre class="bg-gray-900 text-white p-4 rounded-lg mt-1 mb-2 overflow-x-auto whitespace-pre preserve-whitespace"><code class="text-sm font-mono language-${detectedLang}">${formattedCode}</code></pre>`;
  });
  
  // Inline code (must be before other formatting)
  html = html.replace(/`([^`]+)`/gim, '<code class="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-sm font-mono text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600">$1</code>');
  
  // Headers (process from largest to smallest to avoid conflicts)
  html = html.replace(/^###### (.*$)/gim, '<h6 class="text-sm font-semibold mt-3 mb-1 text-black dark:text-gray-100">$1</h6>');
  html = html.replace(/^##### (.*$)/gim, '<h5 class="text-base font-semibold mt-3 mb-1 text-black dark:text-gray-100">$1</h5>');
  html = html.replace(/^#### (.*$)/gim, '<h4 class="text-lg font-semibold mt-3 mb-1 text-black dark:text-gray-100">$1</h4>');
  html = html.replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-4 mb-1 text-black dark:text-gray-100">$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2 class="text-2xl font-semibold mt-5 mb-1 text-black dark:text-gray-100 border-b border-gray-200 dark:border-gray-600 pb-1">$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mt-4 mb-2 text-black dark:text-gray-100 border-b border-gray-300 dark:border-gray-500 pb-1">$1</h1>');
  
  // Bold and Italic (process bold first to avoid conflicts)
  html = html.replace(/\*\*\*(.*?)\*\*\*/gim, '<strong class="font-bold"><em class="italic">$1</em></strong>');
  html = html.replace(/\*\*(.*?)\*\*/gim, '<strong class="font-bold">$1</strong>');
  html = html.replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>');
  html = html.replace(/__(.*?)__/gim, '<strong class="font-bold">$1</strong>');
  html = html.replace(/_(.*?)_/gim, '<em class="italic">$1</em>');
  
  // Strikethrough
  html = html.replace(/~~(.*?)~~/gim, '<del class="line-through text-gray-700 dark:text-gray-400">$1</del>');
  
  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" class="text-blue-800 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 underline transition-colors" target="_blank" rel="noopener noreferrer">$1</a>');
  
  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/gim, '<img src="$2" alt="$1" class="max-w-full h-auto rounded-lg mt-1 mb-2 shadow-md" />');
  
  // Horizontal rules
  html = html.replace(/^---$/gim, '<hr class="mt-2 mb-2 border-gray-300 dark:border-gray-600" />');
  html = html.replace(/^\*\*\*$/gim, '<hr class="mt-2 mb-2 border-gray-300 dark:border-gray-600" />');
  
  // Checkboxes (task lists)
  html = html.replace(/^- \[x\] (.*$)/gim, '<div class="flex items-center gap-2 mb-1"><input type="checkbox" checked disabled class="rounded border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700"><span class="line-through text-gray-700 dark:text-gray-300">$1</span></div>');
  html = html.replace(/^- \[ \] (.*$)/gim, '<div class="flex items-center gap-2 mb-1"><input type="checkbox" disabled class="rounded border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700"><span class="text-black dark:text-gray-100">$1</span></div>');
  
  // Tables (improved handling)
  const lines = html.split('\n');
  let inTable = false;
  let tableHTML = '';
  let processedLines: string[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.includes('|') && line.length > 1) {
      if (!inTable) {
        inTable = true;
        tableHTML = '<div class="overflow-x-auto mt-1 mb-2"><table class="min-w-full border border-gray-300 dark:border-gray-600"><tbody>';
      }
      
      const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell.length > 0);
      
      // Skip separator line
      if (cells.every(cell => /^:?-+:?$/.test(cell))) {
        continue;
      }
      
      // Check if it's header (next line is separator)
      const isHeader = i + 1 < lines.length && lines[i + 1].includes('|') && 
                     lines[i + 1].split('|').every(cell => /^:?-+:?$/.test(cell.trim()));
      
              if (isHeader) {
          tableHTML += '<tr class="bg-gray-50 dark:bg-gray-700">' + 
                      cells.map(cell => `<th class="border border-gray-300 dark:border-gray-600 px-4 py-2 font-semibold text-left text-black dark:text-gray-100">${cell}</th>`).join('') + 
                      '</tr>';
        } else {
          tableHTML += '<tr class="hover:bg-gray-100 dark:hover:bg-gray-700">' + 
                      cells.map(cell => `<td class="border border-gray-300 dark:border-gray-600 px-4 py-2 text-black dark:text-gray-100">${cell}</td>`).join('') + 
                      '</tr>';
        }
    } else {
      if (inTable) {
        tableHTML += '</tbody></table></div>';
        processedLines.push(tableHTML);
        tableHTML = '';
        inTable = false;
      }
      processedLines.push(line);
    }
  }
  
  if (inTable) {
    tableHTML += '</tbody></table></div>';
    processedLines.push(tableHTML);
  }
  
  html = processedLines.join('\n');
  
  // Lists (improved handling with checkboxes)
  const listLines = html.split('\n');
  const resultLines: string[] = [];
  let inList = false;
  let listType = '';
  let listLevel = 0;
  
  for (const line of listLines) {
    const trimmedLine = line.trim();
    const leadingSpaces = line.length - line.trimStart().length;
    const currentLevel = Math.floor(leadingSpaces / 2);
    
    // Skip checkbox lines (already processed)
    if (/^<div class="flex items-center gap-2 mb-1">/.test(trimmedLine)) {
      if (inList) {
        resultLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        inList = false;
      }
      resultLines.push(line);
      continue;
    }
    
    // Unordered list
    if (/^[*\-+] /.test(trimmedLine)) {
      const content = trimmedLine.replace(/^[*\-+] /, '');
      
      if (!inList || listType !== 'ul' || currentLevel !== listLevel) {
        if (inList) resultLines.push('</ul>');
        resultLines.push('<ul class="list-disc list-inside ml-4 mt-1 mb-2 space-y-0">');
        inList = true;
        listType = 'ul';
        listLevel = currentLevel;
      }
      
      resultLines.push(`<li class="text-black dark:text-gray-100 leading-relaxed">${content}</li>`);
    }
    // Ordered list
    else if (/^\d+\. /.test(trimmedLine)) {
      const content = trimmedLine.replace(/^\d+\. /, '');
      
      if (!inList || listType !== 'ol' || currentLevel !== listLevel) {
        if (inList) resultLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        resultLines.push('<ol class="list-decimal list-inside ml-4 mt-1 mb-2 space-y-0">');
        inList = true;
        listType = 'ol';
        listLevel = currentLevel;
      }
      
      resultLines.push(`<li class="text-black dark:text-gray-100">${content}</li>`);
    }
    // Not a list item
    else {
      if (inList) {
        resultLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        inList = false;
      }
      resultLines.push(line);
    }
  }
  
  if (inList) {
    resultLines.push(listType === 'ul' ? '</ul>' : '</ol>');
  }
  
  html = resultLines.join('\n');
  
  // Blockquotes
  html = html.replace(/^&gt; (.+)$/gim, '<blockquote class="border-l-4 border-blue-300 dark:border-blue-600 pl-4 italic text-gray-800 dark:text-gray-300 mt-1 mb-2 bg-blue-50 dark:bg-blue-900/20 py-2">$1</blockquote>');
  
  // Clean up multiple line breaks before processing paragraphs
  html = html.replace(/\n{3,}/gim, '\n\n');
  
  // Paragraphs and line breaks
  html = html.replace(/\n\n/gim, '</p><p class="mb-2">');
  html = html.replace(/\n/gim, '<br/>');
  
  // Wrap in paragraph tags
  if (html && !html.startsWith('<')) {
    html = '<p class="mb-2">' + html + '</p>';
  }
  
  // Clean up empty paragraphs
  html = html.replace(/<p class="mb-2"><\/p>/gim, '');
  html = html.replace(/<p class="mb-2">\s*<\/p>/gim, '');
  html = html.replace(/<p class="mb-2"><br\/><\/p>/gim, '');
  
  // Remove excessive breaks before and after block elements
  html = html.replace(/<br\/>\s*(<h[1-6])/gim, '$1');
  html = html.replace(/<br\/>\s*(<ul)/gim, '$1');
  html = html.replace(/<br\/>\s*(<ol)/gim, '$1');
  html = html.replace(/<br\/>\s*(<blockquote)/gim, '$1');
  html = html.replace(/<br\/>\s*(<div class="overflow-x-auto)/gim, '$1');
  html = html.replace(/(<\/ul>)\s*<br\/>/gim, '$1');
  html = html.replace(/(<\/ol>)\s*<br\/>/gim, '$1');
  html = html.replace(/(<\/blockquote>)\s*<br\/>/gim, '$1');
  html = html.replace(/(<\/div>)\s*<br\/>/gim, '$1');
  
  return html;
};

// Main component
export default function MarkdownDocSystem() {
  const [documents, setDocuments] = useState<MarkdownDoc[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<MarkdownDoc | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewGroupForm, setShowNewGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backupInputRef = useRef<HTMLInputElement>(null);

  // Custom CSS for markdown content with dark mode support
  const markdownStyles = `
    .markdown-content h1, .markdown-content h2, .markdown-content h3, .markdown-content h4, .markdown-content h5, .markdown-content h6 {
      margin-top: 1em;
      margin-bottom: 0.25em;
      line-height: 1.3;
    }
    .markdown-content h1:first-child, .markdown-content h2:first-child, .markdown-content h3:first-child {
      margin-top: 0;
    }
    .markdown-content p {
      margin-bottom: 0.5em;
      line-height: 1.6;
    }
    .markdown-content ul, .markdown-content ol {
      margin: 0.25em 0 0.5em 0;
    }
    .markdown-content li {
      margin: 0;
      line-height: 1.5;
    }
    .markdown-content blockquote {
      margin: 0.5em 0;
    }
    .markdown-content pre {
      margin: 0.75em 0;
    }
    .markdown-content table {
      margin: 0.75em 0;
      border-collapse: collapse;
      width: 100%;
    }
    .markdown-content img {
      margin: 0.75em 0;
    }
    .markdown-content hr {
      margin: 1em 0;
    }
    /* Remove espaços entre títulos e elementos seguintes */
    .markdown-content h1 + p, .markdown-content h2 + p, .markdown-content h3 + p, .markdown-content h4 + p {
      margin-top: 0;
    }
    .markdown-content h1 + ul, .markdown-content h2 + ul, .markdown-content h3 + ul, .markdown-content h4 + ul,
    .markdown-content h1 + ol, .markdown-content h2 + ol, .markdown-content h3 + ol, .markdown-content h4 + ol {
      margin-top: 0;
    }
    .markdown-content h1 + blockquote, .markdown-content h2 + blockquote, .markdown-content h3 + blockquote, .markdown-content h4 + blockquote {
      margin-top: 0;
    }
    /* Espaçamento reduzido para elementos de código */
    .markdown-content p + pre, .markdown-content h1 + pre, .markdown-content h2 + pre, .markdown-content h3 + pre, .markdown-content h4 + pre {
      margin-top: 0.25em;
    }
    /* Melhor contraste para código */
    .markdown-content code {
      font-weight: 500;
    }
    .markdown-content pre {
      background-color: #111827 !important;
      border: 1px solid #374151 !important;
      white-space: pre !important;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
      line-height: 1.4 !important;
    }
    .markdown-content pre code {
      color: #ffffff !important;
      font-weight: normal;
      background: transparent !important;
      border: none !important;
      padding: 0 !important;
      white-space: pre !important;
      font-family: inherit !important;
      line-height: 1.4 !important;
    }
    /* Light mode styles - Cores com melhor contraste */
    .markdown-content {
      color: #000000 !important;
    }
    .markdown-content h1, .markdown-content h2, .markdown-content h3, 
    .markdown-content h4, .markdown-content h5, .markdown-content h6 {
      color: #000000 !important;
    }
    .markdown-content p {
      color: #000000 !important;
    }
    .markdown-content li {
      color: #000000 !important;
    }
    .markdown-content blockquote {
      color: #1f2937 !important;
    }
    .markdown-content strong {
      color: #000000 !important;
    }
    .markdown-content em {
      color: #000000 !important;
    }
    .markdown-content del {
      color: #6b7280 !important;
    }
    .markdown-content a {
      color: #1d4ed8 !important;
    }
    .markdown-content a:hover {
      color: #1e40af !important;
    }
    /* Garantir contraste para todos os elementos de texto */
    .markdown-content div {
      color: #000000 !important;
    }
    .markdown-content span {
      color: inherit !important;
    }
    /* Dark mode styles */
    .dark .markdown-content {
      color: #e5e7eb !important;
    }
    .dark .markdown-content h1, .dark .markdown-content h2, .dark .markdown-content h3, 
    .dark .markdown-content h4, .dark .markdown-content h5, .dark .markdown-content h6 {
      color: #f9fafb !important;
    }
    .dark .markdown-content p {
      color: #e5e7eb !important;
    }
    .dark .markdown-content li {
      color: #e5e7eb !important;
    }
    .dark .markdown-content blockquote {
      color: #d1d5db !important;
    }
    .dark .markdown-content strong {
      color: #f9fafb !important;
    }
    .dark .markdown-content em {
      color: #e5e7eb !important;
    }
    .dark .markdown-content del {
      color: #9ca3af !important;
    }
    .dark .markdown-content a {
      color: #60a5fa !important;
    }
    .dark .markdown-content a:hover {
      color: #93c5fd !important;
    }
    /* Garantir que a indentação seja preservada */
    .markdown-content pre {
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
      word-break: break-all !important;
      overflow-x: auto !important;
      background-color: #111827 !important;
      border: 1px solid #374151 !important;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
    }
    .markdown-content pre code {
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
      word-break: break-all !important;
      color: #ffffff !important;
      background: transparent !important;
      font-family: inherit !important;
    }
    .markdown-content .preserve-whitespace {
      white-space: pre !important;
      tab-size: 2 !important;
    }
    /* Melhor contraste para inline code */
    .markdown-content code:not(pre code) {
      background-color: #f3f4f6 !important;
      color: #000000 !important;
      border: 1px solid #d1d5db !important;
    }
    .dark .markdown-content code:not(pre code) {
      background-color: #374151 !important;
      color: #e5e7eb !important;
      border: 1px solid #4b5563 !important;
    }
    /* Garantir contraste para todos os elementos dentro de pre */
    .markdown-content pre * {
      color: #ffffff !important;
    }
    /* Melhorar contraste das tabelas no modo claro */
    .markdown-content table tr:hover {
      background-color: #f3f4f6 !important;
    }
    .markdown-content table tr:hover td,
    .markdown-content table tr:hover th {
      color: #000000 !important;
    }
    /* Melhorar contraste das listas de tarefas no modo escuro */
    .dark .markdown-content div span {
      color: #e5e7eb !important;
    }
    .dark .markdown-content div span.line-through {
      color: #d1d5db !important;
    }
    /* Responsividade para blocos de código */
    .markdown-content pre {
      max-width: 100%;
      overflow-x: auto;
      white-space: pre-wrap;
      word-wrap: break-word;
      word-break: break-all;
    }
    .markdown-content pre code {
      white-space: pre-wrap;
      word-wrap: break-word;
      word-break: break-all;
    }
    /* Melhorar responsividade em telas pequenas */
    @media (max-width: 768px) {
      .markdown-content {
        font-size: 14px;
      }
      .markdown-content h1 {
        font-size: 1.5rem !important;
      }
      .markdown-content h2 {
        font-size: 1.25rem !important;
      }
      .markdown-content h3 {
        font-size: 1.125rem !important;
      }
      .markdown-content pre {
        font-size: 12px;
        padding: 0.75rem;
      }
    }
    /* Garantir scrolls independentes */
    .h-screen {
      height: 100vh;
      height: 100dvh; /* Dynamic viewport height for mobile */
    }
    /* Melhorar scroll em mobile */
    .overflow-y-auto {
      -webkit-overflow-scrolling: touch;
    }
    /* Garantir que os containers tenham altura definida */
    .min-h-0 {
      min-height: 0;
    }
    /* Garantir que flex items não cresçam além do necessário */
    .flex-shrink-0 {
      flex-shrink: 0;
    }
    /* Garantir que o conteúdo principal tenha scroll independente */
    .flex-1 {
      flex: 1 1 0%;
    }
  `;

  // Load data and theme on component mount
  useEffect(() => {
    loadData();
    loadTheme();
    
    // Add custom styles
    const styleElement = document.createElement('style');
    styleElement.textContent = markdownStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const loadTheme = () => {
    const savedTheme = localStorage.getItem('markdown-doc-theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
    }
  };

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('markdown-doc-theme', newTheme ? 'dark' : 'light');
  };

  const loadData = async () => {
    try {
      const [docs, savedGroups] = await Promise.all([getDocuments(), getGroups()]);
      setDocuments(docs);
      setGroups(savedGroups.length > 0 ? savedGroups : [{ id: 'default', name: 'Documentos', expanded: true }]);
      
      // Se não há documentos, criar o documento de exemplo
      if (docs.length === 0) {
        await createExampleDocument();
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const createExampleDocument = async () => {
    const exampleContent = `# Welcome to Markdown Editor

O código markdown abaixo demonstra várias funcionalidades do markdown. Aproveite!

# Header 1
## Header 2
### Header 3
#### Header 4
##### Header 5
###### Header 6

## Ênfase

**Texto em Negrito**

*Texto em Itálico*

~~Texto Tachado~~

## Listas

### Não Ordenadas
- Item 1
- Item 2
  - Subitem 2.1
  - Subitem 2.2

### Ordenadas
1. Primeiro Item
2. Segundo Item
3. Terceiro Item

### Lista de Tarefas
- [x] Tarefa completa
- [ ] Tarefa pendente
- [ ] Outra tarefa

## Links
[Google](http://www.google.com)

## Imagens
![Markdown Logo](https://markdowneditor.org/images/favicon-32x32.png)

## Citações
> Esta é uma citação. Pode conter múltiplas linhas.

## Linha Horizontal
---

## Código

Código inline \`tem back-ticks ao redor\`.

\`\`\`javascript
var s = "Destaque de sintaxe JavaScript"
alert(s)
\`\`\`

\`\`\`python
s = "Destaque de sintaxe Python"
print(s)
\`\`\`

## Tabelas

| Cabeçalho 1 | Cabeçalho 2 | Cabeçalho 3 |
|-------------|-------------|-------------|
| Linha1Col1  | Linha1Col2  | Linha1Col3  |
| Linha2Col1  | Linha2Col2  | Linha2Col3  |
| Linha3Col1  | Linha3Col2  | Linha3Col3  |

## Mais Exemplos

### Combinações de Formatação
**Texto em negrito com *itálico* dentro**

### Links com Formatação
[**Link em negrito**](http://exemplo.com)

### Código com Linguagem
\`\`\`css
body {
  background-color: #f0f0f0;
  font-family: Arial, sans-serif;
}
\`\`\`

Aproveite escrevendo em Markdown!`;

    const exampleDoc: MarkdownDoc = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      title: 'Exemplo de Markdown',
      content: exampleContent,
      group: 'default',
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    await saveDocument(exampleDoc);
    setDocuments(prev => [...prev, exampleDoc]);
    setSelectedDoc(exampleDoc);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      if (file.type === 'text/markdown' || file.name.endsWith('.md')) {
        processFile(file);
      } else {
        alert('Por favor, selecione apenas arquivos .md válidos.');
      }
    });
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target?.result as string;
      const newDoc: MarkdownDoc = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        title: file.name.replace('.md', ''),
        content,
        group: 'default',
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await saveDocument(newDoc);
      setDocuments(prev => [...prev, newDoc]);
      setSelectedDoc(newDoc);
    };
    reader.readAsText(file);
  };

  const createNewDocument = async () => {
    const newDoc: MarkdownDoc = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      title: 'Novo Documento',
      content: '# Novo Documento\n\nComece a escrever seu markdown aqui...',
      group: 'default',
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    await saveDocument(newDoc);
    setDocuments(prev => [...prev, newDoc]);
    setSelectedDoc(newDoc);
    setIsEditing(true);
    setEditTitle(newDoc.title);
    setEditContent(newDoc.content);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const markdownFiles = files.filter(file => 
      file.type === 'text/markdown' || file.name.endsWith('.md')
    );
    
    if (markdownFiles.length === 0) {
      alert('Por favor, arraste apenas arquivos .md válidos.');
      return;
    }
    
    markdownFiles.forEach(file => {
      processFile(file);
    });
  };

  const createNewGroup = async () => {
    if (!newGroupName.trim()) return;
    
    // Verificar se já existe um grupo com esse nome
    const existingGroup = groups.find(group => 
      group.name.toLowerCase() === newGroupName.trim().toLowerCase()
    );
    
    if (existingGroup) {
      alert('Já existe uma pasta com este nome. Escolha outro nome.');
      return;
    }
    
    const newGroup: Group = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      name: newGroupName.trim(),
      expanded: true
    };
    
    const updatedGroups = [...groups, newGroup];
    setGroups(updatedGroups);
    await saveGroups(updatedGroups);
    setNewGroupName('');
    setShowNewGroupForm(false);
  };

  const toggleGroup = async (groupId: string) => {
    const updatedGroups = groups.map(group => 
      group.id === groupId ? { ...group, expanded: !group.expanded } : group
    );
    setGroups(updatedGroups);
    await saveGroups(updatedGroups);
  };

  const moveDocumentToGroup = async (docId: string, groupId: string) => {
    const updatedDocs = documents.map(doc => 
      doc.id === docId ? { ...doc, group: groupId, updatedAt: new Date() } : doc
    );
    setDocuments(updatedDocs);
    
    const docToUpdate = updatedDocs.find(doc => doc.id === docId);
    if (docToUpdate) {
      await saveDocument(docToUpdate);
    }
  };

  const saveEdit = async () => {
    if (!selectedDoc) return;
    
    const updatedDoc: MarkdownDoc = {
      ...selectedDoc,
      title: editTitle.trim() || selectedDoc.title,
      content: editContent,
      updatedAt: new Date()
    };
    
    await saveDocument(updatedDoc);
    setDocuments(prev => prev.map(doc => doc.id === selectedDoc.id ? updatedDoc : doc));
    setSelectedDoc(updatedDoc);
    setIsEditing(false);
    setEditTitle('');
    setEditContent('');
  };

  const deleteDoc = async (docId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (window.confirm('Tem certeza que deseja excluir este documento?')) {
      try {
        await deleteDocument(docId);
        setDocuments(prev => prev.filter(doc => doc.id !== docId));
        if (selectedDoc?.id === docId) {
          setSelectedDoc(null);
          setIsEditing(false);
        }
        console.log('Documento deletado com sucesso');
      } catch (error) {
        console.error('Erro ao deletar documento:', error);
        alert('Erro ao deletar documento. Tente novamente.');
      }
    }
  };

  const renameGroup = async (groupId: string, newName: string) => {
    if (!newName.trim()) {
      setEditingGroupId(null);
      setEditingGroupName('');
      return;
    }
    
    // Verificar se já existe outro grupo com esse nome
    const existingGroup = groups.find(group => 
      group.id !== groupId && group.name.toLowerCase() === newName.trim().toLowerCase()
    );
    
    if (existingGroup) {
      alert('Já existe uma pasta com este nome. Escolha outro nome.');
      setEditingGroupName(groups.find(g => g.id === groupId)?.name || '');
      return;
    }
    
    const updatedGroups = groups.map(group => 
      group.id === groupId ? { ...group, name: newName.trim() } : group
    );
    setGroups(updatedGroups);
    await saveGroups(updatedGroups);
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  const cancelEditGroup = () => {
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  const deleteGroup = async (groupId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Não permitir deletar o grupo padrão
    if (groupId === 'default') {
      alert('Não é possível deletar o grupo padrão.');
      return;
    }

    const docsInGroup = documents.filter(doc => doc.group === groupId);
    
    let confirmMessage = 'Tem certeza que deseja excluir esta pasta?';
    if (docsInGroup.length > 0) {
      confirmMessage = `Esta pasta contém ${docsInGroup.length} documento(s). Eles serão movidos para a pasta "Documentos". Deseja continuar?`;
    }

    if (window.confirm(confirmMessage)) {
      try {
        // Mover documentos para o grupo padrão
        for (const doc of docsInGroup) {
          const updatedDoc = { ...doc, group: 'default', updatedAt: new Date() };
          await saveDocument(updatedDoc);
        }

        // Atualizar estado dos documentos
        setDocuments(prev => prev.map(doc => 
          doc.group === groupId ? { ...doc, group: 'default', updatedAt: new Date() } : doc
        ));

        // Remover o grupo
        const updatedGroups = groups.filter(group => group.id !== groupId);
        setGroups(updatedGroups);
        await saveGroups(updatedGroups);

        // Se o documento selecionado estava neste grupo, atualizar sua referência
        if (selectedDoc && selectedDoc.group === groupId) {
          setSelectedDoc({ ...selectedDoc, group: 'default' });
        }

        console.log('Pasta deletada com sucesso');
      } catch (error) {
        console.error('Erro ao deletar pasta:', error);
        alert('Erro ao deletar pasta. Tente novamente.');
      }
    }
  };

  const exportBackup = () => {
    try {
      const backup = {
        documents,
        groups,
        exportDate: new Date().toISOString(),
        version: '1.0'
      };
      
      const jsonString = JSON.stringify(backup, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `markdown-docs-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('Backup exportado com sucesso');
    } catch (error) {
      console.error('Erro ao exportar backup:', error);
      alert('Erro ao criar backup. Tente novamente.');
    }
  };

  const importBackup = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      alert('Por favor, selecione um arquivo JSON válido.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const backupContent = e.target?.result as string;
        const backup = JSON.parse(backupContent);
        
        if (!backup.documents || !backup.groups) {
          alert('Arquivo de backup inválido. Certifique-se de que é um backup válido do sistema.');
          return;
        }

        if (window.confirm('Importar backup irá substituir todos os dados atuais. Deseja continuar?')) {
          // Clear existing data
          const db = await initDB();
          const transaction = db.transaction(['documents', 'groups'], 'readwrite');
          await transaction.objectStore('documents').clear();
          await transaction.objectStore('groups').clear();
          
          // Import new data
          for (const doc of backup.documents) {
            await saveDocument(doc);
          }
          await saveGroups(backup.groups);
          
          // Update state
          setDocuments(backup.documents);
          setGroups(backup.groups);
          setSelectedDoc(null);
          setIsEditing(false);
          
          alert('Backup importado com sucesso!');
          console.log('Backup importado com sucesso');
        }
      } catch (error) {
        console.error('Erro ao importar backup:', error);
        alert('Erro ao importar backup. Verifique se o arquivo está correto.');
      }
    };
    
    reader.onerror = () => {
      alert('Erro ao ler o arquivo. Tente novamente.');
    };
    
    reader.readAsText(file);
    
    if (backupInputRef.current) {
      backupInputRef.current.value = '';
    }
  };

  // Função para baixar documento como Markdown
  const downloadAsMarkdown = () => {
    if (!selectedDoc) return;
    
    const blob = new Blob([selectedDoc.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedDoc.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Função para imprimir documento como PDF
  const printAsPDF = () => {
    if (!selectedDoc) return;
    
    // Criar uma nova janela para impressão
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Por favor, permita pop-ups para esta funcionalidade.');
      return;
    }
    
    // HTML para a janela de impressão
    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${selectedDoc.title}</title>
        <meta charset="utf-8">
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            font-size: 14px;
            line-height: 1.6;
            color: #000000;
            background-color: #ffffff;
            margin: 0;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          
          h1 {
            font-size: 28px;
            font-weight: 700;
            color: #000000;
            margin: 0 0 20px 0;
            padding-bottom: 10px;
            border-bottom: 2px solid #3b82f6;
            page-break-after: avoid;
          }
          
          h2 {
            font-size: 24px;
            font-weight: 600;
            color: #000000;
            margin: 30px 0 15px 0;
            page-break-after: avoid;
          }
          
          h3 {
            font-size: 20px;
            font-weight: 600;
            color: #000000;
            margin: 25px 0 12px 0;
            page-break-after: avoid;
          }
          
          h4 {
            font-size: 18px;
            font-weight: 600;
            color: #000000;
            margin: 20px 0 10px 0;
            page-break-after: avoid;
          }
          
          p {
            margin: 12px 0;
            color: #000000;
            line-height: 1.6;
          }
          
          ul, ol {
            margin: 12px 0;
            padding-left: 20px;
          }
          
          li {
            margin: 6px 0;
            color: #000000;
          }
          
          blockquote {
            margin: 20px 0;
            padding: 15px 20px;
            background-color: #f8f9fa;
            border-left: 4px solid #3b82f6;
            color: #374151;
            font-style: italic;
            page-break-inside: avoid;
          }
          
          code {
            background-color: #f3f4f6;
            color: #dc2626;
            padding: 2px 6px;
            border-radius: 4px;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 13px;
          }
          
          pre {
            background-color: #f8f9fa;
            color: #000000;
            padding: 15px;
            border-radius: 6px;
            margin: 15px 0;
            overflow-x: auto;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 13px;
            line-height: 1.4;
            border: 1px solid #e5e7eb;
            page-break-inside: avoid;
          }
          
          pre code {
            background-color: transparent;
            color: #000000;
            padding: 0;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
            page-break-inside: avoid;
          }
          
          th {
            background-color: #f8f9fa;
            color: #000000;
            font-weight: 600;
            padding: 10px;
            border: 1px solid #d1d5db;
            text-align: left;
          }
          
          td {
            padding: 10px;
            border: 1px solid #d1d5db;
            color: #000000;
          }
          
          tr:nth-child(even) {
            background-color: #f8f9fa;
          }
          
          a {
            color: #3b82f6;
            text-decoration: underline;
          }
          
          strong {
            color: #000000;
            font-weight: 600;
          }
          
          em {
            color: #000000;
            font-style: italic;
          }
          
          hr {
            margin: 20px 0;
            border: none;
            border-top: 2px solid #e5e7eb;
            page-break-after: avoid;
          }
          
          img {
            max-width: 100%;
            height: auto;
            margin: 15px 0;
            border-radius: 6px;
            page-break-inside: avoid;
          }
          
          @page {
            margin: 2cm;
            size: A4;
          }
        </style>
      </head>
      <body>
        <div class="markdown-content">
          ${parseMarkdown(selectedDoc.content)}
        </div>
        
        <script>
          // Aguardar carregamento e abrir impressão
          window.onload = function() {
            setTimeout(() => {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;
    
    // Escrever HTML na nova janela
    printWindow.document.write(printHTML);
    printWindow.document.close();
  };



  const filteredDocuments = documents.filter(doc => 
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedDocuments = groups.reduce((acc, group) => {
    acc[group.id] = filteredDocuments.filter(doc => doc.group === group.id);
    return acc;
  }, {} as Record<string, MarkdownDoc[]>);

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header - Fixed */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 p-3 md:p-4 flex-shrink-0 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="lg:hidden flex items-center justify-center w-10 h-10 bg-gray-600 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
              title="Menu"
            >
              {showSidebar ? <ArrowLeft size={16} /> : <Menu size={16} />}
            </button>
            <div className="flex items-center gap-3">
              <img src={logo} alt="DocFlow Logo" className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0" />
              <div className="flex flex-col">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">DocFlow</h1>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Sistema de criação de markdown e organizador</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 md:gap-2 flex-wrap justify-center sm:justify-end">
            <button
              onClick={() => setShowTutorial(!showTutorial)}
              className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
              title="Tutorial Markdown"
            >
              <HelpCircle size={16} className="md:w-4 md:h-4" />
              <span className="hidden md:inline">Tutorial</span>
            </button>
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors text-sm"
              title={isDarkMode ? "Modo Claro" : "Modo Escuro"}
            >
              {isDarkMode ? <Sun size={16} className="md:w-4 md:h-4" /> : <Moon size={16} className="md:w-4 md:h-4" />}
              <span className="hidden md:inline">{isDarkMode ? "Claro" : "Escuro"}</span>
            </button>
            <button
              onClick={createNewDocument}
              className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
              title="Novo Documento"
            >
              <FilePlus size={16} className="md:w-4 md:h-4" />
              <span className="hidden md:inline">Novo</span>
            </button>
            <button
              onClick={exportBackup}
              className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              title="Exportar Backup"
            >
              <Download size={16} className="md:w-4 md:h-4" />
              <span className="hidden md:inline">Backup</span>
            </button>
            <button
              onClick={() => backupInputRef.current?.click()}
              className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              title="Importar Backup"
            >
              <Upload size={16} className="md:w-4 md:h-4" />
              <span className="hidden md:inline">Importar</span>
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
              title="Adicionar Markdown"
            >
              <Plus size={16} className="md:w-4 md:h-4" />
              <span className="hidden md:inline">Adicionar MD</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Layout - Sidebar Toggle */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Tutorial Flutuante */}
        {showTutorial && (
          <div className="fixed top-20 left-2 right-2 md:left-auto md:right-4 w-auto md:w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Tutorial Markdown</h3>
              <button
                onClick={() => setShowTutorial(false)}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-4 text-sm">
              <div>
                <h4 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Cabeçalhos</h4>
                <code className="block bg-gray-100 dark:bg-gray-700 p-2 rounded text-gray-900 dark:text-gray-100"># Título 1<br/>## Título 2<br/>### Título 3</code>
              </div>
              <div>
                <h4 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Formatação</h4>
                <code className="block bg-gray-100 dark:bg-gray-700 p-2 rounded text-gray-900 dark:text-gray-100">**negrito**<br/>*itálico*<br/>~~tachado~~<br/>`código`</code>
              </div>
              <div>
                <h4 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Listas</h4>
                <code className="block bg-gray-100 dark:bg-gray-700 p-2 rounded text-gray-900 dark:text-gray-100">* Item 1<br/>1. Item numerado<br/>- [x] Tarefa completa</code>
              </div>
              <div>
                <h4 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Links e Imagens</h4>
                <code className="block bg-gray-100 dark:bg-gray-700 p-2 rounded text-gray-900 dark:text-gray-100">[texto](url)<br/>![alt](url-imagem)</code>
              </div>
              <div>
                <h4 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Código</h4>
                <code className="block bg-gray-100 dark:bg-gray-700 p-2 rounded text-gray-900 dark:text-gray-100">```javascript<br/>bloco de código<br/>```</code>
              </div>
              <div>
                <h4 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Tabelas</h4>
                <code className="block bg-gray-100 dark:bg-gray-700 p-2 rounded text-gray-900 dark:text-gray-100">| Col1 | Col2 |<br/>|------|------|<br/>| Dado | Dado |</code>
              </div>
            </div>
          </div>
        )}

        {/* Sidebar - Hidden on mobile by default, shown on desktop */}
        <div className={`${showSidebar ? 'flex' : 'hidden'} lg:flex absolute lg:relative inset-0 lg:inset-auto z-40 w-full lg:w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-col flex-shrink-0`}>
          {/* Search */}
          <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar documentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-sm"
              />
            </div>
          </div>

          {/* Groups and Documents */}
          <div className="flex-1 overflow-y-auto p-3 md:p-4 min-h-0">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-700 dark:text-gray-300">Grupos</h3>
              <button
                onClick={() => setShowNewGroupForm(true)}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Criar nova pasta"
              >
                <FolderPlus size={16} />
              </button>
            </div>

            {showNewGroupForm && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <input
                  type="text"
                  placeholder="Nome do grupo"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded mb-2 bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100"
                  onKeyPress={(e) => e.key === 'Enter' && createNewGroup()}
                />
                <div className="flex gap-2">
                  <button
                    onClick={createNewGroup}
                    className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700"
                  >
                    Criar
                  </button>
                  <button
                    onClick={() => {
                      setShowNewGroupForm(false);
                      setNewGroupName('');
                    }}
                    className="px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-400 dark:hover:bg-gray-500"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {groups.map(group => (
              <div key={group.id} className="mb-3 md:mb-4">
                <div className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="flex-shrink-0 p-1 hover:bg-gray-300 dark:hover:bg-gray-500 rounded"
                    >
                      {group.expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <Folder size={16} className="text-yellow-600 flex-shrink-0" />
                    
                    {editingGroupId === group.id ? (
                      <input
                        type="text"
                        value={editingGroupName}
                        onChange={(e) => setEditingGroupName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            renameGroup(group.id, editingGroupName);
                          } else if (e.key === 'Escape') {
                            cancelEditGroup();
                          }
                        }}
                        className="flex-1 px-2 py-1 text-sm bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                        autoFocus
                      />
                    ) : (
                      <span className="font-medium flex-1 truncate text-gray-900 dark:text-gray-100">{group.name}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                      {groupedDocuments[group.id]?.length || 0}
                    </span>
                    
                    {editingGroupId === group.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => renameGroup(group.id, editingGroupName)}
                          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                          title="Salvar"
                        >
                          <Save size={14} />
                        </button>
                        <button
                          onClick={() => cancelEditGroup()}
                          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 rounded"
                          title="Cancelar"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingGroupId(group.id);
                            setEditingGroupName(group.name);
                          }}
                          className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                          title="Renomear pasta"
                        >
                          <Edit3 size={14} />
                        </button>
                        {group.id !== 'default' && (
                          <button
                            onClick={(e) => deleteGroup(group.id, e)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Excluir pasta"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {group.expanded && (
                  <div className="ml-4 md:ml-6 mt-2 space-y-1">
                    {groupedDocuments[group.id]?.length === 0 ? (
                      <div className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
                        Nenhum documento nesta pasta
                      </div>
                    ) : (
                      groupedDocuments[group.id]?.map(doc => (
                        <div
                          key={doc.id}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                            selectedDoc?.id === doc.id ? 'bg-indigo-100 dark:bg-indigo-900/30 border-l-4 border-indigo-500' : ''
                          }`}
                          onClick={() => {
                            setSelectedDoc(doc);
                            setShowSidebar(false); // Close sidebar on mobile when selecting document
                          }}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText size={14} className="text-gray-500 flex-shrink-0" />
                            <span className="text-sm truncate text-gray-900 dark:text-gray-100">{doc.title}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <select
                              value={doc.group}
                              onChange={(e) => moveDocumentToGroup(doc.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs border-none bg-transparent hover:bg-gray-200 dark:hover:bg-gray-600 rounded px-1 text-gray-900 dark:text-gray-100 hidden sm:block"
                              title="Mover para outra pasta"
                            >
                              {groups.map(g => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                              ))}
                            </select>
                            <button
                              onClick={(e) => deleteDoc(doc.id, e)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                              title="Excluir documento"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div 
          className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-900 relative min-h-0 overflow-hidden"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drag & Drop Overlay */}
          {isDragOver && (
            <div className="absolute inset-0 bg-blue-500 bg-opacity-20 border-2 border-dashed border-blue-500 flex items-center justify-center z-40">
              <div className="text-center text-blue-700 dark:text-blue-300">
                <Upload size={48} className="mx-auto mb-4" />
                <p className="text-xl font-semibold">Solte arquivos .md aqui</p>
                <p className="text-sm">Arraste e solte arquivos markdown para importá-los</p>
              </div>
            </div>
          )}
          
          {selectedDoc ? (
            <>
              {/* Document header */}
              <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 md:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 md:gap-4 flex-shrink-0">
                <div className="max-w-7xl mx-auto w-full flex flex-col sm:flex-row sm:items-center justify-between gap-3 md:gap-4">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">{selectedDoc.title}</h2>
                    <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                      Atualizado em {selectedDoc.updatedAt.toLocaleString()}
                    </p>
                  </div>
                                    <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                    {isEditing ? (
                      <>
                        <button
                          onClick={saveEdit}
                          className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                          title="Salvar"
                        >
                          <Save size={16} className="md:w-4 md:h-4" />
                          <span className="hidden md:inline">Salvar</span>
                        </button>
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditTitle('');
                            setEditContent('');
                          }}
                          className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 text-sm"
                          title="Cancelar"
                        >
                          <X size={16} className="md:w-4 md:h-4" />
                          <span className="hidden md:inline">Cancelar</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setIsEditing(true);
                            setEditTitle(selectedDoc.title);
                            setEditContent(selectedDoc.content);
                          }}
                          className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                          title="Editar"
                        >
                          <Edit3 size={16} className="md:w-4 md:h-4" />
                          <span className="hidden md:inline">Editar</span>
                        </button>
                        <button
                          onClick={downloadAsMarkdown}
                          className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                          title="Baixar como Markdown"
                        >
                          <FileTextIcon size={16} className="md:w-4 md:h-4" />
                          <span className="hidden md:inline">MD</span>
                        </button>
                        <button
                          onClick={printAsPDF}
                          className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:gap-1 md:px-2 md:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                          title="Imprimir como PDF"
                        >
                          <FileDown size={16} className="md:w-4 md:h-4" />
                          <span className="hidden md:inline">PDF</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Document content */}
              <div className="flex-1 p-4 md:p-6 overflow-y-auto bg-white dark:bg-gray-800 min-h-0">
                {isEditing ? (
                  <div className="h-full flex flex-col gap-3 md:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Título do Documento
                      </label>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="w-full p-2 md:p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-sm md:text-base lg:text-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Digite o título do documento..."
                      />
                    </div>
                    <div className="flex-1 flex flex-col min-h-0">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Conteúdo Markdown
                      </label>
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="flex-1 p-2 md:p-3 lg:p-4 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs md:text-sm resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Digite seu markdown aqui..."
                      />
                    </div>
                  </div>
                ) : (
                  <div className="max-w-4xl mx-auto">
                    <div 
                      className="markdown-content leading-relaxed"
                      style={{ 
                        lineHeight: '1.7'
                      }}
                      dangerouslySetInnerHTML={{ __html: parseMarkdown(selectedDoc.content) }}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400 p-4">
              <div className="text-center max-w-sm md:max-w-md">
                <FileText size={40} className="md:w-12 md:h-12 mx-auto mb-3 md:mb-4 text-gray-300 dark:text-gray-600" />
                <h2 className="text-lg md:text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">Bem-vindo ao DocFlow</h2>
                <p className="text-sm md:text-base lg:text-lg mb-2">Selecione um documento para visualizar</p>
                <p className="text-xs md:text-sm mb-3 md:mb-4">ou adicione um novo arquivo Markdown</p>
                <div className="space-y-1 md:space-y-2 text-xs">
                  <p>💡 Dica: Arraste arquivos .md para esta área</p>
                  <p>💡 Dica: Use o botão "Novo" para criar um documento</p>
                  <p>💡 Dica: Clique em "Tutorial" para ver comandos markdown</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <span>Sistema desenvolvido por</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">Caynan Ramos</span>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/caynan.qa"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                title="Instagram"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                @caynan.qa
              </a>
              <a
                href="https://www.linkedin.com/in/caynanr/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                title="LinkedIn"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".md,.markdown"
        onChange={handleFileUpload}
        className="hidden"
      />
      <input
        ref={backupInputRef}
        type="file"
        accept=".json"
        onChange={importBackup}
        className="hidden"
      />
    </div>
  );
}